import React, { useState } from "react";
import { useForm } from "react-hook-form";
import {
  User,
  Mail,
  Calendar,
  Shield,
  Package,
  Settings,
  Heart,
  Edit3,
  Save,
  X,
  Phone,
  MapPin,
  LogOut,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../hooks/useWishlist";
import OrderHistory from "./OrderHistory";
import WishlistPage from "../shop/WishlistPage";
import toast from "react-hot-toast";

const Profile = () => {
  const { user, updateProfile, logout } = useAuth();
  const { wishlistCount } = useWishlist();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      firstName: user?.first_name || "",
      lastName: user?.last_name || "",
      phone: user?.phone || "",
      address: user?.address || "",
    },
  });

  const onSubmit = async (data) => {
    try {
      const result = await updateProfile(data);
      if (result.success) {
        setIsEditing(false);
        toast.success("Profile updated successfully!");
      }
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const handleCancel = () => {
    reset({
      firstName: user?.first_name || "",
      lastName: user?.last_name || "",
      phone: user?.phone || "",
      address: user?.address || "",
    });
    setIsEditing(false);
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully!");
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-gradient-to-r from-purple-500 to-purple-600 text-white";
      case "moderator":
        return "bg-gradient-to-r from-blue-500 to-blue-600 text-white";
      case "customer":
        return "bg-gradient-to-r from-green-500 to-green-600 text-white";
      default:
        return "bg-gradient-to-r from-gray-500 to-gray-600 text-white";
    }
  };

  const getStatusBadgeColor = (isActive) => {
    return isActive
      ? "bg-gradient-to-r from-green-500 to-green-600 text-white"
      : "bg-gradient-to-r from-red-500 to-red-600 text-white";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            My Account
          </h1>
          <p className="text-gray-600 text-lg">
            Manage your profile, orders, and preferences
          </p>
        </div>

        {/* Tab Navigation - Mobile Optimized */}
        <div className="mb-8">
          <nav className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => setActiveTab("profile")}
              className={`${
                activeTab === "profile"
                  ? "bg-black text-white shadow-lg transform scale-105"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              } flex items-center justify-center px-6 py-4 rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md`}
            >
              <Settings className="w-5 h-5 mr-3" />
              <span className="hidden sm:inline">Profile Settings</span>
              <span className="sm:hidden">Profile</span>
            </button>
            <button
              onClick={() => setActiveTab("orders")}
              className={`${
                activeTab === "orders"
                  ? "bg-black text-white shadow-lg transform scale-105"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              } flex items-center justify-center px-6 py-4 rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md`}
            >
              <Package className="w-5 h-5 mr-3" />
              <span className="hidden sm:inline">Order History</span>
              <span className="sm:hidden">Orders</span>
            </button>
            <button
              onClick={() => setActiveTab("wishlist")}
              className={`${
                activeTab === "wishlist"
                  ? "bg-black text-white shadow-lg transform scale-105"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
              } flex items-center justify-center px-6 py-4 rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md relative`}
            >
              <Heart className="w-5 h-5 mr-3" />
              <span className="hidden sm:inline">Wishlist</span>
              <span className="sm:hidden">Wishlist</span>
              {wishlistCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                  {wishlistCount}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "profile" && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
              {/* Profile Header */}
              <div className="bg-gradient-to-r from-gray-800 to-black px-6 py-8 text-white">
                <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <div className="relative">
                    <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-4 border-white/30">
                      <span className="text-2xl sm:text-3xl font-bold text-white">
                        {user?.first_name?.[0]}
                        {user?.last_name?.[0]}
                      </span>
                    </div>
                    {!isEditing && (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="absolute -bottom-2 -right-2 bg-white text-black p-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-2">
                      {user?.first_name} {user?.last_name}
                    </h2>
                    <p className="text-gray-200 flex items-center justify-center sm:justify-start">
                      <Mail className="mr-2 h-4 w-4" />
                      {user?.email}
                    </p>
                    <div className="mt-3 flex flex-col sm:flex-row items-center sm:items-start space-y-2 sm:space-y-0 sm:space-x-4">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(
                          user?.role
                        )}`}
                      >
                        <Shield className="mr-2 h-4 w-4" />
                        {user?.role}
                      </span>
                      <span className="text-gray-200 flex items-center text-sm">
                        <Calendar className="mr-2 h-4 w-4" />
                        Member since{" "}
                        {user?.created_at
                          ? new Date(user.created_at).toLocaleDateString()
                          : "Unknown"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Form */}
              {isEditing && (
                <div className="p-6">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          First Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            {...register("firstName", {
                              required: "First name is required",
                              minLength: {
                                value: 2,
                                message:
                                  "First name must be at least 2 characters",
                              },
                            })}
                            type="text"
                            className={`block w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all duration-200 ${
                              errors.firstName
                                ? "border-red-300 bg-red-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            placeholder="Enter your first name"
                          />
                        </div>
                        {errors.firstName && (
                          <p className="mt-2 text-sm text-red-600 flex items-center">
                            <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                            {errors.firstName.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Last Name
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            {...register("lastName", {
                              required: "Last name is required",
                              minLength: {
                                value: 2,
                                message:
                                  "Last name must be at least 2 characters",
                              },
                            })}
                            type="text"
                            className={`block w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all duration-200 ${
                              errors.lastName
                                ? "border-red-300 bg-red-50"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                            placeholder="Enter your last name"
                          />
                        </div>
                        {errors.lastName && (
                          <p className="mt-2 text-sm text-red-600 flex items-center">
                            <span className="w-1 h-1 bg-red-600 rounded-full mr-2"></span>
                            {errors.lastName.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Phone className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            {...register("phone")}
                            type="tel"
                            className="block w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all duration-200 hover:border-gray-300"
                            placeholder="Enter your phone number"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Address
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MapPin className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            {...register("address")}
                            type="text"
                            className="block w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all duration-200 hover:border-gray-300"
                            placeholder="Enter your address"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="flex-1 sm:flex-none bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200 flex items-center justify-center"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 sm:flex-none bg-black text-white py-3 px-6 rounded-xl font-medium hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black transition-all duration-200 flex items-center justify-center shadow-lg hover:shadow-xl"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Account Information */}
              {!isEditing && (
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Account Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <dt className="text-sm font-medium text-gray-500 mb-1">
                        Email Address
                      </dt>
                      <dd className="text-sm text-gray-900 font-medium">
                        {user?.email}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <dt className="text-sm font-medium text-gray-500 mb-1">
                        Account Role
                      </dt>
                      <dd className="text-sm text-gray-900 font-medium capitalize">
                        {user?.role}
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <dt className="text-sm font-medium text-gray-500 mb-1">
                        Account Status
                      </dt>
                      <dd>
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(
                            user?.is_active
                          )}`}
                        >
                          {user?.is_active ? "Active" : "Inactive"}
                        </span>
                      </dd>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-xl">
                      <dt className="text-sm font-medium text-gray-500 mb-1">
                        Last Login
                      </dt>
                      <dd className="text-sm text-gray-900 font-medium">
                        {user?.last_login
                          ? new Date(user.last_login).toLocaleString()
                          : "Never"}
                      </dd>
                    </div>
                    {user?.phone && (
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <dt className="text-sm font-medium text-gray-500 mb-1">
                          Phone Number
                        </dt>
                        <dd className="text-sm text-gray-900 font-medium">
                          {user.phone}
                        </dd>
                      </div>
                    )}
                    {user?.address && (
                      <div className="bg-gray-50 p-4 rounded-xl">
                        <dt className="text-sm font-medium text-gray-500 mb-1">
                          Address
                        </dt>
                        <dd className="text-sm text-gray-900 font-medium">
                          {user.address}
                        </dd>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Logout Button */}
            <div className="mt-8 flex justify-center">
              <button
                onClick={handleLogout}
                className="bg-primary-600 text-white px-8 py-4 rounded-xl font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 flex items-center shadow-lg hover:shadow-xl"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </button>
            </div>
          </div>
        )}

        {activeTab === "orders" && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <OrderHistory />
          </div>
        )}

        {activeTab === "wishlist" && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <WishlistPage />
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
